import type { CSSProperties, MouseEventHandler, ReactNode } from 'react';
import { forwardRef, useRef } from 'react';
import { Link, useHistory } from '@compat/router';

interface BannersLinkProps {
  children: ReactNode;
  link: string;
  state?: Record<string, any>;
  style?: CSSProperties;
  ariaLabel?: string;
  onClick?: MouseEventHandler;
  className?: string;
  openInNewPage?: boolean;
}

type Ref = HTMLAnchorElement;

const BannersLink = forwardRef<Ref, BannersLinkProps>(
  (
    {
      children,
      openInNewPage,
      className,
      link,
      state = null,
      style,
      ariaLabel,
      onClick
    },
    ref
  ) => {
    const history = useHistory();
    const mousePosStart = useRef(0);
    const mousePosEnd = useRef(0);

    const mouseDownDetect: MouseEventHandler = e => {
      mousePosStart.current = e.pageX;
    };
    const mouseUpDetect: MouseEventHandler = e => {
      mousePosEnd.current = e.pageX;
    };
    const compareMouse: MouseEventHandler = e => {
      const difference = Math.abs(mousePosStart.current - mousePosEnd.current);
      e.preventDefault();
      e.stopPropagation();
      if (onClick) {
        onClick(e);
        return;
      }
      if (
        e.target instanceof HTMLElement &&
        e.target.className.includes('popup-overlay')
      ) {
        return;
      }
      if (e.ctrlKey || openInNewPage) {
        window.open(link, '_blank');
      } else {
        if (difference < 10) {
          history.push(link, { ...state, positionY: e.pageY });
        }
      }
    };

    if (link.includes('https://')) {
      return (
        <a
          target='_blank'
          href={link}
          rel='nofollow noreferrer'
          ref={ref}
          draggable={false}
          className={`banners-link ${className ? `${className}` : ''}`}
          style={style ?? {}}
          aria-label={ariaLabel ?? undefined}
        >
          {children}
        </a>
      );
    }
    return (
      <Link
        ref={ref}
        target={openInNewPage ? '_blank' : '_self'}
        draggable={false}
        className={`banners-link ${className ? `${className}` : ''}`}
        onClick={compareMouse}
        onMouseDown={mouseDownDetect}
        onMouseUp={mouseUpDetect}
        to={{ pathname: link, state }}
        style={style ?? {}}
        aria-label={ariaLabel ?? undefined}
      >
        {children}
      </Link>
    );
  }
);

export default BannersLink;
